import React, { useCallback, useEffect, useRef } from 'react';
import { matchPath, useLocation } from 'react-router';
import styled from 'styled-components';

import { FullPageModal } from '#/components/Modal';
import PlanPurchaseFlow from '#/components/PlanPurchaseFlow/PlanPurchaseFlow';
import { PageSpinner } from '#/components/Splash';
import { Status } from '#/components/Status';
import { useNavigation } from '#/hooks/useNavigation';
import { Path } from '#/service/routes';
import { paywallActions, usePaywall } from '#/store/paywall';

import { usePlanPickerActions } from './hooks';
import { useSubscriptionParameters } from './utils';

const HiddenContainer = styled.div`
  display: none;
`;

const Paywall = React.memo(() => {
  const {
    isShowing,
    deferredPromise: { resolve: resolvePayment } = {},
    error,
    plans,
    isPaywallReady,
    isFetchingPlans,
    hasFetchedPlans,
  } = usePaywall();
  const { pathname } = useLocation();
  const prevPathName = useRef<string>(pathname);
  const { goBack } = useNavigation('Paywall');

  const customParameters = useSubscriptionParameters();

  const actions = usePlanPickerActions();

  /**
   * TODO: Actually instead of this, we should have a handler for clicking App Logo on the Modal
   * and then close the Modal. It is quite strange that we navigate user to the home page.
   * We should just hide the full-page Modal.
   */
  useEffect(() => {
    // Only react to the location change, nothing else. Otherwise, we will close the modal just after opening it
    if (isShowing && prevPathName.current !== pathname) {
      paywallActions.closePaywall();
    }
    prevPathName.current = pathname;
  }, [isShowing, pathname]);

  const handleErrorDismiss = useCallback(() => {
    paywallActions.setError(undefined);

    // Navigate to the previous page only if the paywall error
    // was dismissed on the playback page
    if (matchPath(Path.Playback, pathname)) {
      goBack();
    } else {
      paywallActions.closePaywall();
    }
  }, [goBack, pathname]);

  const showPaywall = isShowing || error;
  const isReady = isPaywallReady && !isFetchingPlans && hasFetchedPlans;

  return (
    <div>
      <HiddenContainer className="hidden-paywall-container" />
      {showPaywall && (
        <FullPageModal>
          {error && (
            <Status
              fatalError
              onDismiss={handleErrorDismiss}
              statusMessage={error.message}
              statusTitle={error.title}
            />
          )}
          {isReady ? (
            <PlanPurchaseFlow
              actions={actions}
              customParameters={customParameters}
              hasError={!!error}
              onResult={resolvePayment}
              plans={plans ?? []}
            />
          ) : (
            <PageSpinner $startFadeout={false} data-testid="paywall-spinner" />
          )}
        </FullPageModal>
      )}
    </div>
  );
});

Paywall.displayName = 'Paywall';

export default Paywall;
